import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Button, 
  TextField, 
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Divider
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';

import { useAuth } from '../../components/auth/AuthModule';
import GalleryCard from './components/GalleryCard';
import CreateGalleryModal from './components/CreateGalleryModal';
import { gallery as galleryApi } from '../../utils/api';

const GalleryPage = () => {
  const { t } = useTranslation('gallery');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('-date');
  const [openCreateModal, setOpenCreateModal] = useState(false);
  
  // Fetch galleries with pagination and filters
  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 12,
        sort
      };
      
      if (search) {
        params.search = search;
      }
      
      const data = await galleryApi.getAll(params);
      setGalleries(data.galleries);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Error fetching galleries:', error);
      toast.error(error.message || 'Failed to load galleries');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchGalleries();
  }, [page, sort]);
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
    fetchGalleries();
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  const handleSortChange = (event) => {
    setSort(event.target.value);
  };
  
  const handleOpenCreateModal = () => {
    setOpenCreateModal(true);
  };
  
  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
  };
  
  const handleGalleryCreated = () => {
    fetchGalleries();
    handleCloseCreateModal();
    toast.success(t('messages.createSuccess'));
  };
  
  const handleViewGallery = (id) => {
    navigate(`/galleries/${id}`);
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          
          {user?.isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateModal}
            >
              {t('actions.create')}
            </Button>
          )}
        </Box>
        
        {/* Search and Filter */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <form onSubmit={handleSearch}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t('gallery.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button type="submit" variant="text">
                          {t('gallery.filter')}
                        </Button>
                      </InputAdornment>
                    )
                  }}
                />
              </form>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="sort-select-label">{t('gallery.sort')}</InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={sort}
                  onChange={handleSortChange}
                  label={t('gallery.sort')}
                >
                  <MenuItem value="-date">{t('gallery.sortByDate')} (↓)</MenuItem>
                  <MenuItem value="date">{t('gallery.sortByDate')} (↑)</MenuItem>
                  <MenuItem value="-title">{t('gallery.sortByTitle')} (↓)</MenuItem>
                  <MenuItem value="title">{t('gallery.sortByTitle')} (↑)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        {/* Gallery Grid */}
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" height={30} sx={{ mt: 1 }} />
                <Skeleton variant="text" height={20} width="60%" />
              </Grid>
            ))}
          </Grid>
        ) : galleries.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={3}>
              {galleries.map((gallery) => (
                <Grid item xs={12} sm={6} md={4} key={gallery._id}>
                  <motion.div variants={itemVariants}>
                    <GalleryCard 
                      gallery={gallery} 
                      onView={() => handleViewGallery(gallery._id)}
                      isAdmin={user?.isAdmin}
                      onRefresh={fetchGalleries}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              {t('gallery.noGalleries')}
            </Typography>
          </Box>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}
      </Box>
      
      {/* Create Gallery Modal */}
      {openCreateModal && (
        <CreateGalleryModal
          open={openCreateModal}
          onClose={handleCloseCreateModal}
          onSuccess={handleGalleryCreated}
        />
      )}
    </Container>
  );
};

export default GalleryPage; 